import { useReconciliationEntitled } from '@/modules/account/hooks'
import { cn, numberToCurrency } from '@/utils'
import { monthShortLabel } from '../model/months'
import type { AllAccountsAsOf } from '../model/portfolioOverview'
import { ReasonActionButton } from '../ReasonActionButton'
import { SecuritiesPendingState } from '../SecuritiesPendingState'
import {
  cashLeftOutNote,
  cashNote,
  changeLabel,
  isCashShown,
  noTotalNotes,
  partLabel,
  securitiesNote,
  startedSinceNote,
  totalLabel,
} from './heroText'

const FigurePart = ({
  label,
  value,
  note,
}: {
  label: string
  value: number | null
  note: string | null
}) => (
  <div>
    <div className="text-xs text-ink-subtle">{label}</div>
    <div className="mt-0.5 whitespace-nowrap tabular-nums">
      {numberToCurrency(value ?? 0)}
      {note && <span className="ml-2 text-xs text-ink-subtle">{note}</span>}
    </div>
  </div>
)

const NoTotal = ({ asOf }: { asOf: AllAccountsAsOf }) => {
  const firstMissing = asOf.missing[0]
  return (
    <>
      <div className="mt-2 flex flex-col gap-0.5">
        {noTotalNotes(asOf).map((note) => (
          <p key={note} className="text-xs text-ink-subtle">
            {note}
          </p>
        ))}
      </div>
      {firstMissing && (
        <div className="mt-3">
          <ReasonActionButton
            action={{
              kind: 'upload_statement',
              label: `Upload the ${monthShortLabel(firstMissing.month)} statement`,
              month: firstMissing.month,
            }}
          />
        </div>
      )}
      {asOf.verifying.length > 0 && (
        <div className="mt-4">
          <SecuritiesPendingState
            layout="card"
            detail="The total shows once every statement is verified."
          />
        </div>
      )}
    </>
  )
}

const TotalParts = ({ asOf }: { asOf: AllAccountsAsOf }) => {
  const change = changeLabel(asOf)
  const startedSince = startedSinceNote(asOf)
  const isCashInPlan = useReconciliationEntitled()
  const cashLeftOut = cashLeftOutNote(asOf, isCashInPlan)
  return (
    <>
      <div className="mt-5 flex flex-wrap gap-x-12 gap-y-3">
        <FigurePart
          label={partLabel(asOf, 'Securities')}
          value={asOf.securities}
          note={securitiesNote(asOf)}
        />
        {isCashShown(asOf) && (
          <FigurePart
            label={partLabel(asOf, 'Cash')}
            value={asOf.cash}
            note={cashNote(asOf)}
          />
        )}
      </div>
      {cashLeftOut && (
        <p className="mt-2 text-xs text-ink-subtle">{cashLeftOut}</p>
      )}
      {change && asOf.change && (
        <div className="mt-5 flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
          <span className="text-sm text-ink-subtle">{change}</span>
          <span className="tabular-nums">
            {numberToCurrency(asOf.change.amount)}
          </span>
          {startedSince && (
            <span className="text-xs text-ink-subtle">{startedSince}</span>
          )}
        </div>
      )}
    </>
  )
}

// The one big number: the total on the as-of date, or "No total" with the reason and the fix.
export const HeroFigure = ({ asOf }: { asOf: AllAccountsAsOf }) => (
  <div>
    <div className="text-xs text-ink-subtle">{totalLabel(asOf)}</div>
    <div
      className={cn(
        'mt-0.5 text-[30px] leading-[38px] font-semibold tracking-tight tabular-nums',
        asOf.total == null && 'text-ink-subtle',
      )}
    >
      {asOf.total == null ? 'No total' : numberToCurrency(asOf.total)}
    </div>
    {asOf.total == null ? <NoTotal asOf={asOf} /> : <TotalParts asOf={asOf} />}
  </div>
)
